import { keepPreviousData, useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { CircularProgress } from 'src/components/CircularProgress';
import { TextField } from 'src/components/TextField';
import { Theme } from 'src/components/muiTheme/utils';
import { SxProps } from 'src/components/muiTheme/utils';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { Exercise } from 'src/models/exercise/_types';
import { searchExercises } from 'src/models/exercise/exercise';

interface ExerciseAutocompleteProps {
  value: Exercise | string | null;
  onChange: (exercise: Exercise | null) => void;
  label?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  onEnter?: (value: string) => void;
  // clear options when input is cleared
  clearOptions?: boolean;
}

export const ExerciseAutocomplete: React.FC<ExerciseAutocompleteProps> = ({
  value,
  onChange,
  label = 'Select Exercise',
  placeholder = '',
  sx,
  onEnter,
  clearOptions = false,
}) => {
  const { session } = useOptionalSession();
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<Exercise[]>([]);

  const {
    data: _exercises,
    refetch: refetchExercises,
    isFetching,
  } = useQuery({
    queryKey: ['exercises', 'search', searchValue],

    queryFn: ({ signal }) =>
      searchExercises({
        search: searchValue,
        trainerId: session?.trainerId || '',
        signal,
      }),

    enabled: false,
    placeholderData: keepPreviousData,
    staleTime: 5000,
  });

  const debouncedSearch = debounce(async (value: string) => {
    if (value.length >= 2) {
      const { data } = await refetchExercises();
      setOptions(data || []);
    }
  }, 300);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string
  ) => {
    if (reason === 'clear' && clearOptions) {
      setSearchValue('');
      setOptions([]);
      return;
    }
    if (reason === 'input') {
      setSearchValue(value);

      if (!value && clearOptions) {
        // clear options when input is cleared
        setOptions([]);
        return;
      }

      debouncedSearch(value);
    }
  };

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: Exercise | string) =>
        typeof option === 'string' ? option : option.name
      }
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      loading={isFetching}
      filterOptions={(x) => x}
      noOptionsText={
        searchValue ? 'No exercises found' : 'Start typing to find exercises'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress color="inherit" size="small" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
            onKeyUp: (e) => {
              if (e.key === 'Enter') {
                onEnter?.(searchValue);
              }
            },
            onBlur: () => {
              if (clearOptions && !value) {
                setSearchValue('');
                setOptions([]);
              }
            },
          }}
        />
      )}
      sx={sx}
    />
  );
};
